<template>
  <v-card class="widget-filter-menu">
    <!-- Draggable Header -->
    <div class="widget-filter-menu-header"
      @mousedown="onHeaderMouseDown"
      @mouseup="onHeaderMouseUp">
      <v-icon small class="drag-handle">mdi-drag</v-icon>
      <v-icon small>mdi-filter</v-icon>
      <span class="text-subtitle-2 font-weight-medium">Widget Filters</span>
      <v-spacer></v-spacer>
      <v-chip v-if="activeFilterCount > 0" x-small color="primary" class="white--text"
        style="height: 18px; min-width: 18px;">
        {{ activeFilterCount }}
      </v-chip>
      <v-btn small icon @click.stop="$emit('close')" class="ml-1">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Scrollable body -->
    <div class="widget-filter-menu-body">
      <v-expansion-panels v-model="openPanel" flat accordion>

        <!-- 1. Property Filters -->
        <v-expansion-panel>
          <v-expansion-panel-header class="text-caption font-weight-medium py-0" style="min-height: 40px;">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-format-list-checks</v-icon>
              Property Filters
              <v-chip v-if="propertyFilterCount > 0" x-small color="primary" class="white--text ml-2"
                style="height: 16px; min-width: 16px;">
                {{ propertyFilterCount }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="!filterableColumns.length" class="text-caption text--disabled pa-2">
              No filterable columns available.
            </div>
            <v-expansion-panels v-else flat accordion class="property-panels">
              <v-expansion-panel
                v-for="col in filterableColumns"
                :key="col.name">
                <v-expansion-panel-header class="text-caption py-0" style="min-height: 32px;">
                  <div class="d-flex align-center">
                    <v-icon x-small class="mr-1" color="grey">{{ typeIcon(col.dataType) }}</v-icon>
                    {{ getColumnDisplayName(col.name) }}
                    <v-chip
                      v-if="localFilters.propertyFilters[col.name] && localFilters.propertyFilters[col.name].length"
                      x-small color="primary" class="white--text ml-auto"
                      style="height: 14px; min-width: 14px; font-size: 10px;">
                      {{ localFilters.propertyFilters[col.name].length }}
                    </v-chip>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <widget-property-filter
                    :tableName="sqlViewConfig.tableName"
                    :columnName="col.name"
                    :dataType="col.dataType"
                    :value="localFilters.propertyFilters[col.name] || []"
                    :filterSessionId="filterSessionId"
                    @input="onPropertyFilterChange(col.name, $event)">
                  </widget-property-filter>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- 2. Date Zoom -->
        <v-expansion-panel v-if="dateColumns.length > 0">
          <v-expansion-panel-header class="text-caption font-weight-medium py-0" style="min-height: 40px;">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-calendar-range</v-icon>
              Date Zoom
              <v-chip v-if="localFilters.dateZoom.preset" x-small color="primary" class="white--text ml-2"
                style="height: 16px;">
                1
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-select
              v-model="localFilters.dateZoom.column"
              :items="dateColumns"
              item-text="displayName"
              item-value="name"
              label="Date Column"
              dense
              outlined
              hide-details
              class="mb-2">
            </v-select>
            <v-radio-group v-model="localFilters.dateZoom.preset" dense hide-details class="mt-0">
              <v-radio label="Today" value="today" class="mb-0"></v-radio>
              <v-radio label="This Week" value="thisWeek" class="mb-0"></v-radio>
              <v-radio label="This Month" value="thisMonth" class="mb-0"></v-radio>
              <v-radio label="Last 30 Days" value="last30" class="mb-0"></v-radio>
              <v-radio label="Last 90 Days" value="last90" class="mb-0"></v-radio>
              <v-radio label="Custom Range" value="custom" class="mb-0"></v-radio>
            </v-radio-group>
            <div v-if="localFilters.dateZoom.preset === 'custom'" class="d-flex mt-2" style="gap: 8px;">
              <v-menu :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="localFilters.dateZoom.customStart"
                    label="Start Date"
                    dense outlined hide-details readonly
                    v-bind="attrs" v-on="on"
                    style="flex: 1;">
                  </v-text-field>
                </template>
                <v-date-picker v-model="localFilters.dateZoom.customStart" no-title scrollable></v-date-picker>
              </v-menu>
              <v-menu :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="localFilters.dateZoom.customEnd"
                    label="End Date"
                    dense outlined hide-details readonly
                    v-bind="attrs" v-on="on"
                    style="flex: 1;">
                  </v-text-field>
                </template>
                <v-date-picker v-model="localFilters.dateZoom.customEnd" no-title scrollable></v-date-picker>
              </v-menu>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- 3. Top / Bottom -->
        <v-expansion-panel>
          <v-expansion-panel-header class="text-caption font-weight-medium py-0" style="min-height: 40px;">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-sort-numeric-variant</v-icon>
              Top / Bottom
              <v-chip v-if="localFilters.topBottom.mode" x-small color="primary" class="white--text ml-2"
                style="height: 16px;">
                1
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="isMatrixType" class="widget-filter-locked-note">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              Ordering is managed by this chart type.
            </div>
            <v-select
              v-if="numericColumns.length > 0"
              v-model="localFilters.topBottom.column"
              :items="numericColumns"
              item-text="displayName"
              item-value="name"
              label="Rank by Column"
              dense
              outlined
              hide-details
              :disabled="isMatrixType"
              class="mb-2">
            </v-select>
            <v-radio-group v-model="localFilters.topBottom.mode" dense hide-details :disabled="isMatrixType" class="mt-0">
              <v-radio label="Top 5" value="top5" class="mb-0"></v-radio>
              <v-radio label="Top 10" value="top10" class="mb-0"></v-radio>
              <v-radio label="Bottom 5" value="bottom5" class="mb-0"></v-radio>
              <v-radio label="Bottom 10" value="bottom10" class="mb-0"></v-radio>
              <v-radio label="Show All" :value="null" class="mb-0"></v-radio>
            </v-radio-group>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- 4. Sort -->
        <v-expansion-panel>
          <v-expansion-panel-header class="text-caption font-weight-medium py-0" style="min-height: 40px;">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-sort</v-icon>
              Sort
              <v-chip v-if="localFilters.sort.mode" x-small color="primary" class="white--text ml-2"
                style="height: 16px;">
                1
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="isMatrixType" class="widget-filter-locked-note">
              <v-icon x-small class="mr-1">mdi-information-outline</v-icon>
              Ordering is managed by this chart type.
            </div>
            <v-select
              v-model="localFilters.sort.column"
              :items="allColumns"
              item-text="displayName"
              item-value="name"
              label="Sort Column"
              dense
              outlined
              hide-details
              :disabled="isMatrixType"
              class="mb-2">
            </v-select>
            <v-radio-group v-model="localFilters.sort.mode" dense hide-details :disabled="isMatrixType" class="mt-0">
              <v-radio label="Highest to Lowest" value="highToLow" class="mb-0"></v-radio>
              <v-radio label="Lowest to Highest" value="lowToHigh" class="mb-0"></v-radio>
              <v-radio label="A - Z" value="aToZ" class="mb-0"></v-radio>
              <v-radio label="Z - A" value="zToA" class="mb-0"></v-radio>
              <v-radio label="Newest" value="newest" class="mb-0"></v-radio>
              <v-radio label="Oldest" value="oldest" class="mb-0"></v-radio>
            </v-radio-group>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- 5. Show / Hide Blanks -->
        <v-expansion-panel>
          <v-expansion-panel-header class="text-caption font-weight-medium py-0" style="min-height: 40px;">
            <div class="d-flex align-center">
              <v-icon small class="mr-2">mdi-eye-off-outline</v-icon>
              Show / Hide Blanks
              <v-chip v-if="localFilters.blanks.mode === 'exclude'" x-small color="primary" class="white--text ml-2"
                style="height: 16px;">
                1
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-radio-group v-model="localFilters.blanks.mode" dense hide-details class="mt-0">
              <v-radio label="Include Blanks" value="include" class="mb-0"></v-radio>
              <v-radio label="Exclude Blanks" value="exclude" class="mb-0"></v-radio>
            </v-radio-group>
          </v-expansion-panel-content>
        </v-expansion-panel>

      </v-expansion-panels>
    </div>

    <!-- Footer -->
    <div class="widget-filter-menu-footer">
      <v-btn small text color="error" @click="reset">
        <v-icon small class="mr-1">mdi-filter-remove</v-icon>
        Reset Widget View
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn small :outlined="activeFilterCount === 0" :color="activeFilterCount > 0 ? 'secondary' : ''" :class="{ 'white--text': activeFilterCount > 0 }" @click="apply">
        Apply
      </v-btn>
    </div>
  </v-card>
</template>

<script>
import WidgetPropertyFilter from './WidgetPropertyFilter.vue';
import { MATRIX_TYPES } from '@/features/Dashboard/charts/echartsOptions';

export default {
  name: 'WidgetFilterMenu',

  components: {
    WidgetPropertyFilter,
  },

  props: {
    sqlViewConfig: { type: Object, required: true },
    columns: { type: Array, default: () => [] },
    value: { type: Object, default: () => ({}) },
    filterSessionId: { type: String, default: null },
    // Widget messageType — matrix chart types bake a persisted TopBottom, so their runtime
    // Top/Bottom + Sort controls are foreclosed (see isMatrixType).
    messageType: { type: String, default: '' },
  },

  data() {
    return {
      openPanel: null,
      localFilters: this.buildLocalFilters(this.value),
    };
  },

  computed: {
    filterableColumns() {
      return this.columns.filter(c => c.dataType !== 'uniqueidentifier');
    },

    dateColumns() {
      return this.columns
        .filter(c => c.dataType === 'datetime' || c.dataType === 'date')
        .map(c => ({ name: c.name, displayName: this.getColumnDisplayName(c.name) }));
    },

    numericColumns() {
      const numericTypes = ['int', 'decimal', 'float', 'bigint', 'smallint', 'numeric', 'money'];
      return this.columns
        .filter(c => numericTypes.includes(c.dataType))
        .map(c => ({ name: c.name, displayName: this.getColumnDisplayName(c.name) }));
    },

    // Matrix chart types (stacked column, dual-axis line) bake a persisted TopBottom that the backend
    // honors OVER any runtime widgetFilters on the chart /{id}/query path (SqlViewQueryService.cs:
    // 329-330, request.X ?? widgetFilters.X) and that outranks runtime Sort for ORDER BY (:1321). The
    // menu's Top/Bottom + Sort controls CANNOT work for them, so they are disabled with a note. This
    // is deliberately matrix-ONLY: datagrids use the client-composed /tables/data path where runtime
    // ordering genuinely works (a baked default is only a reload-restored default, not a lock), so
    // their controls must stay live. Value filters stay live regardless.
    isMatrixType() {
      return MATRIX_TYPES.includes(this.messageType);
    },

    allColumns() {
      return this.columns.map(c => ({ name: c.name, displayName: this.getColumnDisplayName(c.name) }));
    },

    propertyFilterCount() {
      return Object.values(this.localFilters.propertyFilters)
        .filter(vals => vals && vals.length > 0).length;
    },

    activeFilterCount() {
      let count = this.propertyFilterCount;
      if (this.localFilters.dateZoom.preset) count++;
      if (this.localFilters.topBottom.mode) count++;
      if (this.localFilters.sort.mode) count++;
      if (this.localFilters.blanks.mode === 'exclude') count++;
      return count;
    },
  },

  watch: {
    value: {
      handler(val) {
        this.localFilters = this.buildLocalFilters(val);
      },
      deep: true,
    },
    activeFilterCount(count) {
      this.$emit('filterCountChange', count);
    },
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDocMouseMove);
    document.removeEventListener('mouseup', this.onDocMouseUp);
  },

  methods: {
    onHeaderMouseDown(e) {
      // Only drag from header itself, not from child buttons
      if (e.target.closest('.v-btn')) return;
      this.$emit('dragstart', e);
      document.addEventListener('mousemove', this.onDocMouseMove);
      document.addEventListener('mouseup', this.onDocMouseUp);
    },
    onHeaderMouseUp(e) {
      this.$emit('dragend', e);
    },
    onDocMouseMove(e) {
      this.$emit('dragmove', e);
    },
    onDocMouseUp(e) {
      this.$emit('dragend', e);
      document.removeEventListener('mousemove', this.onDocMouseMove);
      document.removeEventListener('mouseup', this.onDocMouseUp);
    },

    buildLocalFilters(source) {
      const defaults = {
        propertyFilters: {},
        dateZoom: { column: null, preset: null, customStart: null, customEnd: null },
        topBottom: { mode: null, column: null },
        sort: { mode: null, column: null },
        blanks: { mode: 'include', column: null },
      };
      if (!source) return defaults;
      return {
        propertyFilters: { ...(source.propertyFilters || {}) },
        dateZoom: { ...defaults.dateZoom, ...(source.dateZoom || {}) },
        topBottom: { ...defaults.topBottom, ...(source.topBottom || {}) },
        sort: { ...defaults.sort, ...(source.sort || {}) },
        blanks: { ...defaults.blanks, ...(source.blanks || {}) },
      };
    },

    getColumnDisplayName(name) {
      if (this.sqlViewConfig?.columnDisplayNames?.[name]) {
        return this.sqlViewConfig.columnDisplayNames[name];
      }
      // Convert camelCase to Title Case
      return name.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
    },

    typeIcon(dataType) {
      const numericTypes = ['int', 'decimal', 'float', 'bigint', 'smallint', 'numeric', 'money'];
      if (numericTypes.includes(dataType)) return 'mdi-numeric';
      if (dataType === 'datetime' || dataType === 'date') return 'mdi-calendar';
      if (dataType === 'bit') return 'mdi-toggle-switch';
      return 'mdi-format-text';
    },

    onPropertyFilterChange(columnName, selectedValues) {
      if (selectedValues && selectedValues.length > 0) {
        this.$set(this.localFilters.propertyFilters, columnName, selectedValues);
      } else {
        this.$delete(this.localFilters.propertyFilters, columnName);
      }
    },

    apply() {
      const snapshot = JSON.parse(JSON.stringify(this.localFilters));
      this.$emit('input', snapshot);
      this.$emit('apply', snapshot);
    },

    reset() {
      this.localFilters = this.buildLocalFilters(null);
      const snapshot = JSON.parse(JSON.stringify(this.localFilters));
      this.$emit('input', snapshot);
      this.$emit('reset', snapshot);
    },
  },
};
</script>

<style lang="scss" scoped>
.widget-filter-menu {
  display: flex;
  flex-direction: column;
  max-height: 520px;
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-md, 8px);
  overflow: hidden;

  // Elevated shadow for floating panel
  &.v-card {
    box-shadow: var(--ds-shadow-3, 0 4px 16px rgba(0, 0, 0, 0.5)) !important;
  }

  // Force body font on expansion panel headers (global rule sets heading font)
  ::v-deep .v-expansion-panel-header {
    font-family: var(--ds-font-family-body) !important;
  }

  .widget-filter-menu-header {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 10px 10px 14px;
    cursor: grab;
    user-select: none;
    flex-shrink: 0;
    background: var(--ds-surface-elevated);
    border-bottom: 1px solid var(--ds-border-subtle);

    &:active {
      cursor: grabbing;
    }

    .drag-handle {
      opacity: 0.4;
    }
  }

  .widget-filter-menu-body {
    flex: 1 1 auto;
    overflow-y: auto;
    min-height: 0; // Required for flex child to respect overflow
    padding: 0;
  }

  .widget-filter-menu-footer {
    display: flex;
    align-items: center;
    padding: 10px 14px;
    flex-shrink: 0;
    background: var(--ds-surface-elevated);
    border-top: 1px solid var(--ds-border-subtle);
  }

  .v-expansion-panel-header {
    padding: 8px 16px;
    min-height: 40px;
  }

  .v-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 16px 8px;
  }

  .property-panels {
    .v-expansion-panel-header {
      padding: 4px 12px;
      min-height: 32px;
    }

    .v-expansion-panel-content ::v-deep .v-expansion-panel-content__wrap {
      padding: 0 12px 4px;
    }
  }

  .v-radio {
    margin-bottom: 0 !important;

    ::v-deep .v-label {
      font-size: 12px;
    }
  }

  // Note shown when Top/Bottom + Sort are foreclosed on a matrix chart type. Flat, muted — no
  // accent bar or tinted wash.
  .widget-filter-locked-note {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    font-size: 11.5px;
    line-height: 1.35;
    color: var(--v-text-base);
    opacity: 0.85;
  }
}
</style>
